import { Box, Button, Checkbox, MenuItem, Typography } from '@mui/material';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { FormCard } from 'components/Card';
import { AppAutocompleteField, AppSelectField } from 'components/form-fields';
import { getTrans } from 'modules/i18n';
import { EducationEntry } from 'utils/cv-form-db/types';
import { ButtonContainer } from '../../components/styledComponents';
import { initialValues } from './constants';
import {
  useDegreesNames,
  useEducationForm,
  useStudiesNames,
  useUniversitiesNames
} from './hooks';
import { validateEducation } from './utils';

interface EducationFormProps {
  mode?: 'add' | 'edit';
  initialValues?: EducationEntry;
  onSubmit: (education: EducationEntry) => void;
  onCancel: () => void;
}

const SubmitEducationButton = ({
  isEditMode,
  isComplete
}: {
  isEditMode: boolean;
  isComplete: boolean;
}) => (
  <ButtonContainer sx={{ pb: 1 }}>
    <Button
      fullWidth
      type="submit"
      data-testid={
        isEditMode ? 'save-education-button' : 'add-education-button'
      }
      color="secondary"
      variant="contained"
      sx={{ minWidth: 252 }}
      disabled={!isComplete}
    >
      {isEditMode
        ? getTrans('general.update')
        : getTrans('cvBuilderPage.education.form.button.addEducation.label')}
    </Button>
  </ButtonContainer>
);

const CancelEducationButton = ({ onCancel }: { onCancel: () => void }) => (
  <ButtonContainer sx={{ mt: 2 }}>
    <Button
      fullWidth
      data-testid="cancel-education-button"
      color="secondary"
      variant="outlined"
      sx={{ minWidth: 252 }}
      onClick={onCancel}
    >
      {getTrans('general.cancel')}
    </Button>
  </ButtonContainer>
);

const InnerEducationForm = ({
  mode = 'add',
  onCancel
}: {
  mode: 'add' | 'edit';
  onCancel: () => void;
}) => {
  const { values } = useFormikContext<EducationEntry>();
  const { data: universities = [] } = useUniversitiesNames();
  const { data: degrees = [] } = useDegreesNames();
  const { data: studies = [] } = useStudiesNames();

  const isEditMode = mode === 'edit';

  const { semesterOptions, endDateOptions, isComplete } =
    useEducationForm(values);

  return (
    <Form
      data-testid={`${isEditMode ? 'edit' : 'add'}-education-form`}
      style={{
        flexBasis: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <FormCard>
        <AppAutocompleteField
          name="university"
          label={getTrans('cvBuilderPage.education.form.university.label')}
          options={universities || []}
          freeSolo={false}
        />
        <AppAutocompleteField
          name="study"
          label={getTrans('cvBuilderPage.education.form.study.label')}
          options={studies || []}
          freeSolo={true}
        />
        <AppSelectField
          fullWidth
          displayValidationError
          data-dd-privacy="mask"
          variant="outlined"
          name="studyStart"
          label={getTrans('cvBuilderPage.education.form.startDate.label')}
        >
          {semesterOptions.map(option => (
            <MenuItem
              key={option.value}
              data-testid={`studyStart-select-field-option-${option.value}`}
              value={option.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </AppSelectField>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 2 }}>
          <Field as={Checkbox} type="checkbox" name="currentlyEnrolled" />
          <Typography>
            {getTrans('cvBuilderPage.education.form.currentlyEnrolled.label')}
          </Typography>
        </Box>
        <AppSelectField
          fullWidth
          displayValidationError
          data-dd-privacy="mask"
          variant="outlined"
          name="studyEnd"
          disabled={!values.studyStart}
          label={getTrans(
            values.currentlyEnrolled
              ? 'cvBuilderPage.education.form.expectedEndDate.label'
              : 'cvBuilderPage.education.form.endDate.label'
          )}
        >
          {endDateOptions.map(option => (
            <MenuItem
              data-testid={`studyEnd-select-field-option-${option.value}`}
              key={option.value}
              value={option.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </AppSelectField>
        <Box sx={{ pb: 3 }} />
        <AppSelectField
          fullWidth
          displayValidationError
          data-dd-privacy="mask"
          variant="outlined"
          name="degree"
          label={getTrans(
            values.currentlyEnrolled
              ? 'cvBuilderPage.education.form.expectedDegree.label'
              : 'cvBuilderPage.education.form.degree.label'
          )}
        >
          {degrees.map(degree => (
            <MenuItem
              data-testid={`degree-select-field-option-${degree}`}
              key={degree}
              value={degree}
            >
              {degree}
            </MenuItem>
          ))}
        </AppSelectField>
      </FormCard>
      <Box sx={{ flex: 1 }} />
      <SubmitEducationButton isEditMode={isEditMode} isComplete={isComplete} />
      <CancelEducationButton onCancel={onCancel} />
    </Form>
  );
};

export const EducationForm = ({
  mode = 'add',
  initialValues: editedValues,
  onSubmit,
  onCancel
}: EducationFormProps) => (
  <Formik
    initialValues={editedValues || initialValues}
    onSubmit={onSubmit}
    validate={validateEducation}
    enableReinitialize
  >
    <InnerEducationForm mode={mode} onCancel={onCancel} />
  </Formik>
);
